<template>
  <section
    class="w-[280px] bg-bg-base-bg1 shadow-card rounded-20 transform-gpu will-change-contents transition-all"
  >
    <div
      v-if="currentReactionPage === 'innerReactionsToChoose' && isShortReactionsView"
      class="box-border flex h-14 items-center justify-between gap-0.5 px-2"
    >
      <div
        v-for="reactionData in reactionsToRender"
        :key="reactionData.emoji"
        class="relative rounded-8"
      >
        <button
          :class="{
            'hover:scale-100! rounded-4 border-blue-dashed': reactionData.isSetByCurrentUser,
            'text-[14px]!': reactionData.emoji.length >= 3
          }"
          data-test="toggle-reaction-btn"
          class="cursor-pointer flex justify-center items-center text-[24px] w-8 h-8 z-10 relative hover:scale-[1.3] transition-all transform-gpu hover:will-change"
          @click="toggleReaction(reactionData)"
        >
          {{ reactionData.emoji }}
        </button>
      </div>
      <wui-btn
        data-test="toggle-reaction-view"
        class="justify-self-center shrink-0"
        prepend-icon="chevronDownLgM"
        :size="32"
        variant="neut"
        state="alpha"
        :title="$t('common.showMore')"
        icon
        @click="toggleReactionsToShow"
      >
        <span class="sr-only select-none">Следующие реакции</span>
      </wui-btn>
    </div>

    <div
      v-else-if="currentReactionPage === 'innerReactionsToChoose'"
      class="scrollbar-style px-1 overflow-hidden grid rounded-16 grid-cols-6 gap-y-1 mx-1 max-h-[190px] items-center transition-all overflow-y-auto"
      @scroll="onScroll"
    >
      <div class="flex gap-x-1 py-2 items-center col-span-6 sticky top-0 z-11 rounded-10">
        <wui-input
          v-model="search"
          type="text"
          :placeholder="$t('common.search')"
          with-clear
          prepend-icon="searchM"
          class="rounded-20! min-w-0 grow"
        />
        <wui-btn
          v-if="!forFastReaction"
          data-test="toggle-reaction-view"
          variant="neut"
          state="alpha"
          :size="32"
          prepend-icon="closeRegularMdM"
          icon
          :title="$t('common.showMenu')"
          @click="toggleReactionsToShow"
        >
          <span class="sr-only select-none">Следующие реакции</span>
        </wui-btn>
      </div>
      <div
        v-for="reactionData in reactionsToRender"
        :key="reactionData.emoji"
        class="relative px-[2px] rounded-8"
      >
        <button
          :class="{
            'hover:scale-100! rounded-4 border-blue-dashed': reactionData.isSetByCurrentUser,
            'text-[14px]!': reactionData.emoji.length >= 3
          }"
          data-test="toggle-reaction-btn"
          class="cursor-pointer flex justify-center items-center text-[24px] w-8 h-8 z-10 relative hover:scale-[1.3] transition-all transform-gpu hover:will-change"
          @click="toggleReaction(reactionData)"
        >
          {{ reactionData.emoji }}
        </button>
      </div>
      <div
        v-if="!reactionsToRender.length"
        class="col-span-6 my-4 text-1316 font-light text-center"
      >
        {{ $t('common.nothingFound') }}
      </div>
    </div>
    <custom-reactions-dialog
      v-if="currentReactionPage === 'customTextReactions'"
      :event-reactions="eventReactions!"
      @close-reaction-menu="$emit('closeReactionMenu')"
      @set-reaction="toggleCustomReaction"
      @back-to-inner-reactions="currentReactionPage = 'innerReactionsToChoose'"
    />
    <div
      v-if="!isShortReactionsView"
      class="flex justify-center mt-2 items-center py-1 gap-x-1 border-t border-bor-nav"
    >
      <wui-btn
        icon
        :title="$t('chat.smiles')"
        prepend-icon="faceSmileM"
        variant="neut"
        state="alpha"
        :size="32"
        :class="currentReactionPage === 'innerReactionsToChoose' ? 'text-surf-brand-base-def! hover:bg-surf-neut-soft-hov!' : 'text-fg-base-fg22! hover:bg-surf-neut-soft-hov! '"
        @click="toggleReactionPage('innerReactionsToChoose')"
      />
      <p
        :title="$t('chat.textReactions')"
        class="rounded-8 p-1! w-8 h-8 flex items-center justify-center cursor-pointer text-[20px]"
        :class="currentReactionPage === 'customTextReactions' ? 'text-surf-brand-base-def hover:bg-surf-neut-soft-hov' : 'text-fg-base-fg22 hover:bg-surf-neut-soft-hov '"
        @click="toggleReactionPage('customTextReactions')"
      >
        A
      </p>
    </div>
  </section>
</template>

<script setup lang="ts">
import { WuiBtn, WuiInput } from '@wui/common-library'
import { PServed, ReactionMessagePayload } from '@wui/im'
import { storeToRefs } from 'pinia'
import { computed, onUnmounted, ref, watch } from 'vue'

import { CustomReactionsDialog } from '@/entities/custom-reactions-dialog'
import { useReactionsStore } from '@/entities/reaction'

import { EmittedReaction } from '@/shared/types/emitted-reaction'

const { allEmojisKeywordsMap } = await import('@/shared/utils/emoji-catalog')

const props = defineProps<{
  pServed?: PServed
  eventId?: number
  forFastReaction?: boolean
}>()

const emit = defineEmits<{
  (event: 'closeReactionMenu'): void
}>()

type ReactionPage = 'innerReactionsToChoose' | 'customTextReactions'

const SHORT_REACTIONS_COUNT = 6

const reactionsStore = useReactionsStore()
const { allReactionsToShow, isShortReactionsView, reactionEvents, favoriteFastReaction } = storeToRefs(reactionsStore)
const { setFavoriteReactions } = reactionsStore

const search = ref('')
const currentReactionPage = ref<ReactionPage>('innerReactionsToChoose')

const eventReactions = computed(() => {
  if (props.eventId) {
    return reactionEvents.value.get(props.eventId)
  }
  return null
})

const allReactionsToChoose = computed<EmittedReaction[]>(() => {
  return allReactionsToShow.value.map((emoji) => {
    if (eventReactions.value?.get(emoji)?.isSetByCurrentUser) {
      return { emoji, isSetByCurrentUser: true }
    } else {
      return { emoji, isSetByCurrentUser: false }
    }
  })
})

const reactionsToRender = computed(() => {
  if (isShortReactionsView.value) {
    return allReactionsToChoose.value.slice(0, SHORT_REACTIONS_COUNT)
  }
  if (search.value) {
    const toSearch = search.value.toLocaleLowerCase()
    return allReactionsToChoose.value.filter(reaction =>
      allEmojisKeywordsMap.get(reaction.emoji)?.toLocaleLowerCase()
        .includes(toSearch) || reaction.emoji
        .includes(toSearch))
  } else {
    return allReactionsToChoose.value.slice(0, showReactionsQuantity.value)
  }
})

const toggleReaction = (reactionData: EmittedReaction) => {
  if (props.forFastReaction) {
    favoriteFastReaction.value = reactionData.emoji
    return
  }
  if (props.eventId && props.pServed) {
    setFavoriteReactions(reactionData.emoji)

    const payload: ReactionMessagePayload = {
      to: props.pServed,
      targetEventId: props.eventId,
      reactionType: reactionData.emoji,
    }

    if (reactionData.isSetByCurrentUser) {
      reactionsStore.removeOutgoingReaction(payload)
    } else {
      reactionsStore.makeOutgoingReaction(payload)
    }
    emit('closeReactionMenu')
  }

}

const toggleCustomReaction = (customTextReaction: EmittedReaction) => {
  if (props.forFastReaction) {
    favoriteFastReaction.value = customTextReaction.emoji
  }
  toggleReaction(customTextReaction)
}

const toggleReactionPage = (pageName: ReactionPage) => {
  currentReactionPage.value = pageName
}

const toggleReactionsToShow = () => {
  if (!isShortReactionsView.value) {
    search.value = ''
  }
  return isShortReactionsView.value = !isShortReactionsView.value
}

const showReactionsQuantity = ref(40)

const onScroll = (e: Event) => {
  if (allReactionsToChoose.value.length <= reactionsToRender.value.length) return
  const scrolledEvent = e.target as HTMLElement
  if (scrolledEvent.offsetHeight + scrolledEvent.scrollTop > scrolledEvent.scrollHeight - 100) {
    showReactionsQuantity.value = Math.min(allReactionsToChoose.value.length, showReactionsQuantity.value + 10)
  }
}

onUnmounted(() => {
  isShortReactionsView.value = true
})

watch(
  () => props.forFastReaction,
  (val) => {
    if (val) {
      isShortReactionsView.value = false
    }
  },
  { immediate: true },
)

watch(() => props.eventId, (v) => {
  if (v) {
    isShortReactionsView.value = true
    currentReactionPage.value = 'innerReactionsToChoose'
  }
})

</script>

<style scoped>
@reference "../../../app/assets/tailwind.css";

.scrollbar-style {
  -ms-overflow-style: none;
  scrollbar-width: none;
}

.border-blue-dashed {
  position: relative;
  --n: 4;   /* сколько палок в границе */
  --d: 45deg; /* расстояние между палками */
}

.border-blue-dashed::after {
  @apply bg-hover absolute rounded-4 p-[2%];
  inset: 0;
  content: "";
  mask:
      linear-gradient(#0000 0 0) content-box,
      repeating-conic-gradient(
         from calc(var(--d)/2),
         #000  0 calc(360deg/var(--n) - var(--d)),
         #0000 0 calc(360deg/var(--n))
       );
  mask-composite: intersect;
}
</style>
